import React, { useState, useEffect, useRef, useContext } from 'react';
import { Trash2, Plus, X, ChevronDown, ChevronUp, Sprout } from 'lucide-react';
import { getObjectTypeById, PROPERTY_TYPES, getColorPropertyKey, getBuildingRoofForm } from '../../../config';
import { GardenSettingsContext } from '../../../context/GardenSettingsContext';
import { WIZARD_TO_CATALOG_SOIL_MAP } from '../../../utils/plantFilters';
import {
  PropertyTextField,
  PropertyNumberField,
  PropertySelectField,
  PropertyColorField,
  PropertyBooleanField,
  PropertySliderField,
} from './PropertyFields';
import { ModifyObjectCommand } from '../../../commands/ModifyObjectCommand';
import ConfirmDialog from '../../ui/ConfirmDialog';
import { GARDEN_OBJECT_TYPES } from '../../../constants/gardenObjectTypes';
import { calculateLineProperties } from '../tools/LineTool';
import {
  extractControlPointsFromPath,
  calculatePolygonArea,
  calculatePolygonPerimeter,
  pointsToBezierPath,
} from '../tools/BezierTool';
import { hasSelfIntersection } from '../tools/PolygonTool';
import { applyWegGeometry } from '../tools/wegPath';
import { restoreTerraceRoofSymbolRendering } from '../tools/terraceRoofSymbolRenderer';
import { useLawnTypes, getLawnTypeInfo, resolveLawnTypeValue } from '../../../hooks/useLawnTypes';
import { textureManager } from '../../../lib/textures/textureManager';
import { supportsTexture, SYMBOL_ONLY_TYPES } from '../../../lib/textures/materialMappings';

/**
 * Live-instance `type` of a Fabric.js Path.
 *
 * NOTE: Fabric v6 exposes the *static* class property as `Path.type === 'Path'`
 * (capital P), but a live instance reads through a getter that lowercases it
 * (`FabricObject.get type()` → `this.constructor.type.toLowerCase()`), so
 * `pathInstance.type === 'path'`. Always compare against this constant when
 * matching a selected (live) object — comparing against 'Path' never matches.
 */
const FABRIC_PATH_TYPE = 'path';

// #693: Die Detailfelder der Terrassen-Überdachung hängen an der gewählten
// Variante — jede Variante ist ein anderes Bauwerk mit eigenen Angaben. Ohne
// dieses Gating zeigte das Panel bei „Keine" folgenlose Felder, und die
// Markisen-Felder erschienen beim Terrassendach (und umgekehrt).
//
// Dachform und Dachmaterial teilen sich Terrassendach und Wintergarten (beide
// haben ein formbares, verglasbares Dach); die Traufhöhe (dachHoehe) gehört nur
// zum aufgeständerten Terrassendach, die Wandhöhe nur zum Wintergarten.
const TERRACE_SHARED_ROOF_KEYS = ['dachform', 'dachMaterial'];
const TERRACE_ROOF_ONLY_KEYS = ['dachHoehe'];
const TERRACE_AWNING_DETAIL_KEYS = ['markiseAusfall', 'markiseHoehe', 'markiseAusgefahren'];
const TERRACE_WINTERGARTEN_KEYS = ['wintergartenWandhoehe'];

/**
 * Soll ein mvp-Feld im Panel sichtbar sein? (konditionale Felder, #693/#720)
 *
 * @param {string} objectTypeId
 * @param {string} propertyKey
 * @param {Object} propertyValues - aktuelle mvp-Werte (für abhängige Felder)
 * @param {string} [shapeType] - data.shapeType des Objekts (Legacy-Abgrenzung #720)
 */
export function isPropertyFieldVisible(objectTypeId, propertyKey, propertyValues, shapeType) {
  // #720: Breite + Verlauf gehören nur zum neuen Mittellinien-Weg
  // (shapeType 'centerline'). Bestandswege (Legacy-Fläche aus einer geschlossenen
  // Outline) haben kein wirksames Breiten-/Verlauf-Feld — es würde ins Leere gehen.
  if (objectTypeId === 'weg' && (propertyKey === 'width' || propertyKey === 'verlauf')) {
    return shapeType === 'centerline';
  }
  if (objectTypeId === 'terrasse') {
    const variant = propertyValues?.ueberdachung;
    if (TERRACE_SHARED_ROOF_KEYS.includes(propertyKey)) {
      return variant === 'terrassendach' || variant === 'wintergarten';
    }
    if (TERRACE_ROOF_ONLY_KEYS.includes(propertyKey)) {
      return variant === 'terrassendach';
    }
    if (TERRACE_AWNING_DETAIL_KEYS.includes(propertyKey)) {
      return variant === 'markise';
    }
    if (TERRACE_WINTERGARTEN_KEYS.includes(propertyKey)) {
      return variant === 'wintergarten';
    }
  }
  return true;
}

/**
 * Info box showing description and care notes for a selected lawn type.
 */
const LawnTypeInfoBox = ({ lawnTypes, selectedValue }) => {
  if (!selectedValue) return null;
  const info = getLawnTypeInfo(lawnTypes, selectedValue);
  if (!info) return null;

  return (
    <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm">
      {info.latinName && (
        <p className="text-gray-500 italic text-xs mb-1">{info.latinName}</p>
      )}
      {info.notes && (
        <p className="text-gray-700 mb-1">{info.notes}</p>
      )}
      {info.careNotes && (
        <p className="text-emerald-700 font-medium">{info.careNotes}</p>
      )}
    </div>
  );
};

/**
 * Mapping of property keys to canvas dimensions
 * These properties sync with the actual drawn shape dimensions
 *
 * Jeder Schlüssel muss eine `mvp`-Property in gardenObjectTypes.js sein —
 * geprüft in config/__tests__/gardenObjectPropertyUsage.test.js (#696). Sieben
 * Einträge (`boxLength`, `potWidth`, `potDiameter`, `pondWidth`, `pondLength`,
 * `buildingWidth`, `buildingLength`) bezeichneten Properties, die es nie gab,
 * und wurden entfernt.
 */
export const CANVAS_DIMENSION_MAPPING = {
  boxWidth: 'width',      // Blumenkasten „Länge" → rectangle width (horizontal)
  boxHeight: 'height',    // Blumenkasten „Breite" → rectangle height (horizontal, NICHT vertikal — die Höhe ist boxDepth)
};

/**
 * Properties that should be displayed in a 2-column grid
 * Based on design mockups showing width/depth side-by-side
 *
 * Gleiche Regel wie CANVAS_DIMENSION_MAPPING: nur echte `mvp`-Schlüssel.
 */
export const GRID_LAYOUT_GROUPS = {
  dimensions: [
    'boxWidth', 'boxDepth', 'boxHeight',
    'potHeight',
    'diameter',  // Blumenampel, Topf
    'depth',     // Teich
  ],
};

/**
 * DynamicGardenObjectProperties - Enhanced dynamic properties panel for garden objects
 *
 * Features:
 * - Syncs canvas dimensions with property fields (bidirectional)
 * - 2-column grid layout for dimension fields
 * - Debounced undo/redo support
 * - Auto-updates when canvas object is scaled/moved
 *
 * @param {Object} selectedObject - Selected Fabric.js object
 * @param {Function} onUpdate - Callback when properties change
 * @param {Function} onDelete - Callback when delete button clicked
 * @param {Object} commandStack - Command stack for undo/redo
 * @param {number} pixelsPerCm - Conversion factor (default: 1)
 */
const DynamicGardenObjectProperties = ({
  selectedObject,
  onUpdate,
  onDelete,
  commandStack,
  pixelsPerCm = 1,
  onOpenCatalog,
}) => {
  // Gartenweite Bodenart für den Beet-Fall „Vom Garten übernehmen" (#700).
  // Defensiv über den Roh-Context statt useGardenSettings gelesen, damit das
  // Panel auch ohne Provider (Unit-Tests) rendert.
  const gardenSettings = useContext(GardenSettingsContext);
  const gardenSoilType = gardenSettings?.settings?.soilType ?? null;
  const [propertyValues, setPropertyValues] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isPositionCollapsed, setIsPositionCollapsed] = useState(true); // Collapsed by default for cleaner UI
  const [lineCoordinates, setLineCoordinates] = useState(null); // Local state for line endpoints
  const [bezierControlPoints, setBezierControlPoints] = useState([]); // Local state for bezier control points
  const [bezierError, setBezierError] = useState('');
  const [polygonVertices, setPolygonVertices] = useState([]); // Local state for polygon vertices
  const [polygonError, setPolygonError] = useState('');
  const [rectangleDimensions, setRectangleDimensions] = useState({ width: 0, height: 0 }); // Local state for rectangle dimensions
  const [rectanglePosition, setRectanglePosition] = useState({ x: 0, y: 0 }); // Local state for rectangle position
  const [circleDimensions, setCircleDimensions] = useState({ radiusX: 0, radiusY: 0 }); // Local state for circle radii
  const [circlePosition, setCirclePosition] = useState({ x: 0, y: 0 }); // Local state for circle position
  const { lawnTypes, loading: lawnTypesLoading } = useLawnTypes();

  const debounceTimerRef = useRef(null);
  const oldPropsRef = useRef(null);
  const lineDebounceTimerRef = useRef(null); // Separate debounce timer for line coordinates
  const bezierDebounceTimerRef = useRef(null); // Separate debounce timer for bezier control points
  const polygonDebounceTimerRef = useRef(null); // Separate debounce timer for polygon vertices
  const rectangleDebounceTimerRef = useRef(null); // Separate debounce timer for rectangle dimensions/position
  const circleDebounceTimerRef = useRef(null); // Separate debounce timer for circle dimensions/position

  // Bezier curve constants
  const MIN_CONTROL_POINTS = 3;
  const MAX_CONTROL_POINTS = 100;

  // Polygon constants
  const MIN_VERTICES = 3;
  const MAX_VERTICES = 100;

  // Get object type config
  const objectTypeId = selectedObject?.data?.gardenObjectType || selectedObject?.data?.objectTypeId;
  const objectConfig = objectTypeId ? getObjectTypeById(objectTypeId) : null;

  /**
   * Initialize and sync property values from canvas object
   */
  const syncPropertiesFromCanvas = React.useCallback(() => {
    if (!selectedObject || !objectConfig) return;

    const mvpProperties = objectConfig.properties?.mvp || {};
    const values = {};

    Object.keys(mvpProperties).forEach((key) => {
      const propDef = mvpProperties[key];

      // Check if this property maps to a canvas dimension
      const canvasDimension = CANVAS_DIMENSION_MAPPING[key];

      if (canvasDimension) {
        // Sync from canvas dimensions
        if (selectedObject.objectType === GARDEN_OBJECT_TYPES.RECTANGLE) {
          if (canvasDimension === 'width') {
            values[key] = Math.round((selectedObject.width * selectedObject.scaleX) / pixelsPerCm);
          } else if (canvasDimension === 'height') {
            values[key] = Math.round((selectedObject.height * selectedObject.scaleY) / pixelsPerCm);
          }
        } else if (selectedObject.objectType === GARDEN_OBJECT_TYPES.CIRCLE) {
          if (canvasDimension === 'width') {
            values[key] = Math.round((selectedObject.rx * 2 * selectedObject.scaleX) / pixelsPerCm);
          }
        }
      } else {
        // Get from object data or use default
        values[key] = selectedObject.data?.[key] ?? propDef.defaultValue;
      }
    });

    // Gebäude-Dachform (#689): ist sie nicht ausdrücklich gesetzt, zeigt das
    // Dropdown die aus dem Gebäude-Typ abgeleitete Form — so stimmt die Anzeige
    // mit der 3D-Darstellung überein (der Adapter leitet identisch ab). Erst eine
    // Nutzerauswahl schreibt data.dachform und übersteuert die Ableitung.
    if (objectTypeId === 'gebaeude' && selectedObject.data?.dachform == null) {
      values.dachform = getBuildingRoofForm(selectedObject.data);
    }

    // Also sync useCustomColor from object data (not a config-defined property)
    values.useCustomColor = selectedObject.data?.useCustomColor || false;

    setPropertyValues(values);
  }, [selectedObject, objectConfig, objectTypeId, pixelsPerCm]);

  // Initialize all object-derived local form state when the selected object or
  // scale changes. Done during render via previous-value tracking (not in effects)
  // so there is no synchronous setState-in-effect cascade. Live canvas edits are
  // handled separately by the 'modified'/'scaling' listener below.
  // prevInitKey is seeded with ppc:null so the very first render always runs the
  // block (mount initialization), matching the old effects that fired on mount.
  const [prevInitKey, setPrevInitKey] = useState({ obj: null, ppc: null });
  if (prevInitKey.obj !== selectedObject || prevInitKey.ppc !== pixelsPerCm) {
    setPrevInitKey({ obj: selectedObject, ppc: pixelsPerCm });

    syncPropertiesFromCanvas();

    // Line coordinates
    if (selectedObject?.objectType === GARDEN_OBJECT_TYPES.LINE) {
      setLineCoordinates({
        x1: Math.round(selectedObject.x1 / pixelsPerCm),
        y1: Math.round(selectedObject.y1 / pixelsPerCm),
        x2: Math.round(selectedObject.x2 / pixelsPerCm),
        y2: Math.round(selectedObject.y2 / pixelsPerCm),
        // Pure fallback (no Date.now()) since this runs during render; objectId is
        // only ever written, never read, so a stable null is sufficient here.
        objectId: selectedObject._objects?.[0]?.id ?? null,
      });
    } else {
      setLineCoordinates(null);
    }

    // Bezier control points
    if (selectedObject?.objectType === GARDEN_OBJECT_TYPES.BEZIER_CURVE && selectedObject.type === FABRIC_PATH_TYPE) {
      const pointsInPixels = selectedObject.data?.controlPoints || extractControlPointsFromPath(selectedObject.path);
      setBezierControlPoints(pointsInPixels.map(p => ({
        x: Math.round(p.x / pixelsPerCm),
        y: Math.round(p.y / pixelsPerCm),
      })));
      setBezierError('');
    } else {
      setBezierControlPoints([]);
      setBezierError('');
    }

    // Polygon vertices
    if (selectedObject?.objectType === GARDEN_OBJECT_TYPES.POLYGON && selectedObject.points) {
      setPolygonVertices(selectedObject.points.map(v => ({
        x: Math.round(v.x / pixelsPerCm),
        y: Math.round(v.y / pixelsPerCm),
      })));
      setPolygonError('');
    } else {
      setPolygonVertices([]);
      setPolygonError('');
    }

    // Rectangle dimensions and position
    if (selectedObject?.objectType === GARDEN_OBJECT_TYPES.RECTANGLE) {
      setRectangleDimensions({
        width: Math.round((selectedObject.width * selectedObject.scaleX) / pixelsPerCm),
        height: Math.round((selectedObject.height * selectedObject.scaleY) / pixelsPerCm),
      });
      setRectanglePosition({
        x: Math.round(selectedObject.left / pixelsPerCm),
        y: Math.round(selectedObject.top / pixelsPerCm),
      });
    } else {
      setRectangleDimensions({ width: 0, height: 0 });
      setRectanglePosition({ x: 0, y: 0 });
    }

    // Circle dimensions and position
    if (selectedObject?.objectType === GARDEN_OBJECT_TYPES.CIRCLE) {
      setCircleDimensions({
        radiusX: Math.round((selectedObject.rx * selectedObject.scaleX) / pixelsPerCm),
        radiusY: Math.round((selectedObject.ry * selectedObject.scaleY) / pixelsPerCm),
      });
      setCirclePosition({
        x: Math.round(selectedObject.left / pixelsPerCm),
        y: Math.round(selectedObject.top / pixelsPerCm),
      });
    } else {
      setCircleDimensions({ radiusX: 0, radiusY: 0 });
      setCirclePosition({ x: 0, y: 0 });
    }
  }

  // Listen to canvas modifications to sync dimensions
  useEffect(() => {
    if (!selectedObject) return;

    const handleModified = () => {
      syncPropertiesFromCanvas();

      // Sync line coordinates if it's a line
      if (selectedObject.objectType === GARDEN_OBJECT_TYPES.LINE) {
        setLineCoordinates({
          x1: Math.round(selectedObject.x1 / pixelsPerCm),
          y1: Math.round(selectedObject.y1 / pixelsPerCm),
          x2: Math.round(selectedObject.x2 / pixelsPerCm),
          y2: Math.round(selectedObject.y2 / pixelsPerCm),
          objectId: selectedObject._objects?.[0]?.id || Date.now(),
        });
      }

      // Sync rectangle dimensions and position if it's a rectangle
      if (selectedObject.objectType === GARDEN_OBJECT_TYPES.RECTANGLE) {
        const widthCm = Math.round((selectedObject.width * selectedObject.scaleX) / pixelsPerCm);
        const heightCm = Math.round((selectedObject.height * selectedObject.scaleY) / pixelsPerCm);
        const xCm = Math.round(selectedObject.left / pixelsPerCm);
        const yCm = Math.round(selectedObject.top / pixelsPerCm);

        setRectangleDimensions({ width: widthCm, height: heightCm });
        setRectanglePosition({ x: xCm, y: yCm });
      }

      // Sync circle dimensions and position if it's a circle
      if (selectedObject.objectType === GARDEN_OBJECT_TYPES.CIRCLE) {
        const radiusXCm = Math.round((selectedObject.rx * selectedObject.scaleX) / pixelsPerCm);
        const radiusYCm = Math.round((selectedObject.ry * selectedObject.scaleY) / pixelsPerCm);
        const xCm = Math.round(selectedObject.left / pixelsPerCm);
        const yCm = Math.round(selectedObject.top / pixelsPerCm);

        setCircleDimensions({ radiusX: radiusXCm, radiusY: radiusYCm });
        setCirclePosition({ x: xCm, y: yCm });
      }
    };

    selectedObject.on('modified', handleModified);
    selectedObject.on('scaling', handleModified);

    return () => {
      selectedObject.off('modified', handleModified);
      selectedObject.off('scaling', handleModified);
    };
  }, [selectedObject, pixelsPerCm, syncPropertiesFromCanvas]);

  /**
   * Handle property value change with canvas sync
   */
  /**
   * „Pflanzen für dieses Beet" (#700): öffnet den Katalog vorgefiltert auf die
   * Bodenart des Beets. 'inherit'/unbekannt fällt auf die gartenweite
   * Wizard-Bodenart zurück; lässt sich nichts auflösen, öffnet der Katalog
   * ungefiltert (der Nutzer kann drinnen weiterfiltern).
   */
  const handleOpenBeetPlants = () => {
    if (!onOpenCatalog) return;
    const beetSoil = propertyValues.soilType;
    const effective = !beetSoil || beetSoil === 'inherit' ? gardenSoilType : beetSoil;
    const mapped = effective ? WIZARD_TO_CATALOG_SOIL_MAP[effective] : null;
    onOpenCatalog(mapped ? { soil_type: mapped } : null);
  };

  /**
   * Snapshot der Objekt-Props für Undo/Redo einer Eigenschaftsänderung.
   *
   * Beim Mittellinien-Weg (#720) gehören Stroke-Breite, Verlaufs-Join und der
   * Pfad selbst dazu: applyWegGeometry ändert sie live aus data.width/verlauf;
   * ohne sie im Snapshot driftete die Darstellung nach einem Undo von den
   * (per data restaurierten) Breiten-/Verlauf-Werten weg.
   */
  const buildPropsSnapshot = () => {
    const snap = {
      data: { ...selectedObject.data },
      fill: selectedObject.fill,
      objectCaching: selectedObject.objectCaching,
      width: selectedObject.width,
      height: selectedObject.height,
      scaleX: selectedObject.scaleX,
      scaleY: selectedObject.scaleY,
      rx: selectedObject.rx,
      ry: selectedObject.ry,
    };
    if (selectedObject.data?.shapeType === 'centerline') {
      snap.strokeWidth = selectedObject.strokeWidth;
      snap.strokeLineJoin = selectedObject.strokeLineJoin;
      snap.path = selectedObject.path;
    }
    return snap;
  };

  const handlePropertyChange = (propertyKey, newValue) => {
    // Update local state immediately
    setPropertyValues(prev => ({
      ...prev,
      [propertyKey]: newValue,
    }));

    // Store old state on first change
    if (!oldPropsRef.current) {
      oldPropsRef.current = buildPropsSnapshot();
    }

    // Check if this property maps to canvas dimensions
    const canvasDimension = CANVAS_DIMENSION_MAPPING[propertyKey];

    if (canvasDimension && selectedObject.objectType === GARDEN_OBJECT_TYPES.RECTANGLE) {
      // Update canvas dimensions
      if (canvasDimension === 'width') {
        selectedObject.set({
          width: newValue * pixelsPerCm,
          scaleX: 1,
        });
      } else if (canvasDimension === 'height') {
        selectedObject.set({
          height: newValue * pixelsPerCm,
          scaleY: 1,
        });
      }
      selectedObject.setCoords();
    } else if (canvasDimension && selectedObject.objectType === GARDEN_OBJECT_TYPES.CIRCLE) {
      // Update circle dimensions
      if (canvasDimension === 'width') {
        selectedObject.set({
          rx: (newValue / 2) * pixelsPerCm,
          scaleX: 1,
        });
      }
      selectedObject.setCoords();
    } else {
      // Regular property (not canvas-linked) - just update data
      // Direct assignment (Fabric.js doesn't handle nested objects well with .set())
      // eslint-disable-next-line react-hooks/immutability -- imperative Fabric.js mutation in an event handler; .set() doesn't deep-merge nested data
      selectedObject.data = {
        ...selectedObject.data,
        [propertyKey]: newValue,
      };

      // --- Texture sync ---
      // Farbschlüssel aus der Config ableiten (color, oder wallColor beim Gebäude,
      // #691), damit auch dessen Wandfarbe als Volltonfarbe im Canvas landet.
      const objectType = selectedObject.data?.objectType || selectedObject.data?.gardenObjectType;
      const colorKey = getColorPropertyKey(objectType);
      if (objectType && supportsTexture(objectType)) {
        if (propertyKey === 'material') {
          // Material changed → apply new texture (unless custom color is active)
          if (!selectedObject.data.useCustomColor) {
            textureManager.applyTexture(selectedObject, selectedObject.canvas);
          }
        } else if (propertyKey === colorKey && selectedObject.data.useCustomColor) {
          // Color changed while custom color is active → apply solid color
          textureManager.applySolidColor(selectedObject, newValue, selectedObject.canvas);
        } else if (propertyKey === 'useCustomColor') {
          if (newValue) {
            // Switched to custom color → apply solid color
            const color =
              (colorKey && selectedObject.data[colorKey]) ||
              objectConfig?.defaultStyle?.fill ||
              '#CCCCCC';
            textureManager.applySolidColor(selectedObject, color, selectedObject.canvas);
          } else {
            // Switched back to material texture → re-apply texture
            textureManager.applyTexture(selectedObject, selectedObject.canvas);
          }
        }
      }

      // #693: the terrace roofing select drives the plan symbol. Install (or
      // refresh) the overlay renderer; it reads `data.ueberdachung` live, so
      // switching to 'keine' clears the symbol on the next render.
      if (objectType === 'terrasse' && propertyKey === 'ueberdachung') {
        restoreTerraceRoofSymbolRendering(selectedObject);
      }

      // #720: Breite/Verlauf des Mittellinien-Wegs wirken sofort — Stroke-Breite
      // und Pfadform (eckig/kurvig) aus den frisch gesetzten data-Werten neu
      // ableiten. Legacy-Wege (kein shapeType 'centerline') bleiben unberührt.
      if (
        selectedObject.data?.shapeType === 'centerline' &&
        (propertyKey === 'width' || propertyKey === 'verlauf')
      ) {
        applyWegGeometry(selectedObject);
      }
    }

    // Trigger canvas re-render
    onUpdate?.();

    // Debounce command creation (500ms)
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      if (oldPropsRef.current && commandStack) {
        const newProps = buildPropsSnapshot();

        const command = new ModifyObjectCommand(
          selectedObject,
          oldPropsRef.current,
          newProps
        );
        commandStack.execute(command);

        oldPropsRef.current = null;

        // Fire modified event AFTER command is executed to trigger auto-save
        // This ensures the save happens with the correct data
        if (selectedObject.canvas) {
          selectedObject.canvas.fire('object:modified', { target: selectedObject });
        }
      }
      debounceTimerRef.current = null;
    }, 500);
  };

  // Cleanup: flush pending commands on unmount
  useEffect(() => {
    return () => {
      // Flush property changes
      if (debounceTimerRef.current && oldPropsRef.current && commandStack) {
        clearTimeout(debounceTimerRef.current);

        const newProps = {
          data: { ...selectedObject.data },
          width: selectedObject.width,
          height: selectedObject.height,
          scaleX: selectedObject.scaleX,
          scaleY: selectedObject.scaleY,
        };
        // #720: Weg-Geometrie in den finalen Snapshot übernehmen (siehe
        // buildPropsSnapshot), damit ein Undo nach diesem Flush nicht driftet.
        if (selectedObject.data?.shapeType === 'centerline') {
          newProps.strokeWidth = selectedObject.strokeWidth;
          newProps.strokeLineJoin = selectedObject.strokeLineJoin;
          newProps.path = selectedObject.path;
        }

        const command = new ModifyObjectCommand(
          selectedObject,
          oldPropsRef.current,
          newProps
        );
        commandStack.execute(command);

        oldPropsRef.current = null;
      }

      // Flush line coordinate changes
      if (lineDebounceTimerRef.current && oldPropsRef.current && commandStack) {
        clearTimeout(lineDebounceTimerRef.current);

        const newProps = {
          x1: selectedObject.x1,
          y1: selectedObject.y1,
          x2: selectedObject.x2,
          y2: selectedObject.y2,
        };

        const command = new ModifyObjectCommand(
          selectedObject,
          oldPropsRef.current,
          newProps
        );
        commandStack.execute(command);

        oldPropsRef.current = null;
      }

      // Flush bezier control point changes
      if (bezierDebounceTimerRef.current && oldPropsRef.current && commandStack) {
        clearTimeout(bezierDebounceTimerRef.current);

        const newProps = {
          path: selectedObject.path,
          data: { ...selectedObject.data },
        };

        const command = new ModifyObjectCommand(
          selectedObject,
          oldPropsRef.current.old,
          newProps
        );
        commandStack.execute(command);

        oldPropsRef.current = null;
      }

      // Flush polygon vertex changes
      if (polygonDebounceTimerRef.current && oldPropsRef.current && commandStack) {
        clearTimeout(polygonDebounceTimerRef.current);

        const newProps = {
          points: selectedObject.points,
          data: { ...selectedObject.data },
        };

        const command = new ModifyObjectCommand(
          selectedObject,
          oldPropsRef.current.old,
          newProps
        );
        commandStack.execute(command);

        oldPropsRef.current = null;
      }

      // Flush rectangle dimension/position changes
      if (rectangleDebounceTimerRef.current && oldPropsRef.current && commandStack) {
        clearTimeout(rectangleDebounceTimerRef.current);

        const newProps = {
          width: selectedObject.width,
          height: selectedObject.height,
          scaleX: selectedObject.scaleX,
          scaleY: selectedObject.scaleY,
          left: selectedObject.left,
          top: selectedObject.top,
        };

        const command = new ModifyObjectCommand(
          selectedObject,
          oldPropsRef.current.old,
          newProps
        );
        commandStack.execute(command);

        oldPropsRef.current = null;
      }

      // Flush circle dimension/position changes
      if (circleDebounceTimerRef.current && oldPropsRef.current && commandStack) {
        clearTimeout(circleDebounceTimerRef.current);

        const newProps = {
          rx: selectedObject.rx,
          ry: selectedObject.ry,
          scaleX: selectedObject.scaleX,
          scaleY: selectedObject.scaleY,
          left: selectedObject.left,
          top: selectedObject.top,
        };

        const command = new ModifyObjectCommand(
          selectedObject,
          oldPropsRef.current.old,
          newProps
        );
        commandStack.execute(command);

        oldPropsRef.current = null;
      }
    };
  }, [selectedObject, commandStack]);

  /**
   * Render field based on property type
   */
  const renderField = (propertyKey, propDef) => {
    const value = propertyValues[propertyKey];
    const onChange = (newValue) => handlePropertyChange(propertyKey, newValue);

    switch (propDef.type) {
      case PROPERTY_TYPES.TEXT:
        return (
          <PropertyTextField
            key={propertyKey}
            label={propDef.label}
            value={value}
            onChange={onChange}
            placeholder={propDef.placeholder}
          />
        );

      case PROPERTY_TYPES.NUMBER:
        return (
          <PropertyNumberField
            key={propertyKey}
            label={propDef.label}
            value={value}
            onChange={onChange}
            unit={propDef.unit}
            min={propDef.min}
            max={propDef.max}
            step={propDef.step}
          />
        );

      case PROPERTY_TYPES.SELECT: {
        if (propDef.dynamicOptions === 'lawnTypes') {
          const resolvedValue = resolveLawnTypeValue(value || '');
          const dynamicOpts = lawnTypesLoading
            ? [{ value: '', label: 'Wird geladen...' }]
            : [
                { value: '', label: propDef.placeholder || 'Bitte wählen...' },
                ...lawnTypes.map(lt => ({ value: lt.value, label: lt.label })),
              ];
          return (
            <div key={propertyKey}>
              <PropertySelectField
                label={propDef.label}
                value={resolvedValue}
                onChange={onChange}
                options={dynamicOpts}
                disabled={lawnTypesLoading}
              />
              <LawnTypeInfoBox lawnTypes={lawnTypes} selectedValue={resolvedValue} />
            </div>
          );
        }
        // Beet-Bodenart (#700): unter der Auswahl ein Einstieg in den Katalog,
        // vorgefiltert auf die Bodenart dieses Beets. Der Katalog ist sonst nur
        // gartenweit erreichbar — hier bezieht er sich auf genau dieses Beet.
        if (objectTypeId === 'beet' && propertyKey === 'soilType') {
          return (
            <div key={propertyKey}>
              <PropertySelectField
                label={propDef.label}
                value={value}
                onChange={onChange}
                options={propDef.options}
              />
              {onOpenCatalog && (
                <button
                  type="button"
                  onClick={handleOpenBeetPlants}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-md border border-emerald-600 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
                >
                  <Sprout size={16} />
                  Pflanzen für dieses Beet
                </button>
              )}
            </div>
          );
        }
        return (
          <PropertySelectField
            key={propertyKey}
            label={propDef.label}
            value={value}
            onChange={onChange}
            options={propDef.options}
          />
        );
      }

      case PROPERTY_TYPES.COLOR: {
        // Der „eigene Farbe"-Schalter entscheidet, ob die Nutzerfarbe die kuratierte
        // 3D-Palette (bzw. die Material-Textur) übersteuert. Er gehört zu JEDEM
        // Farbfeld, nicht nur zu Textur-Typen — die frühere Kopplung an
        // hasMaterial && supportsTexture sperrte die Hälfte aller Typen aus (#691).
        // Ausgenommen bleiben die Symbol-Öffnungen (tor/fenster): ihre Farbe zeichnet
        // ihr eigenes _render, und in 3D sind sie bewusst fix (Türpaneel / Glas).
        // Der Umschalter gehört nur ans PRIMÄRE Farbfeld (getColorPropertyKey):
        // Er steuert, ob die Nutzerfarbe die kuratierte 3D-Palette bzw. Textur
        // übersteuert — ein Zustand, den es pro Objekt nur einmal gibt. Ein zweites
        // Farbfeld wie die Dachfarbe (#722) wirkt ohnehin immer und träfe sonst
        // denselben geteilten useCustomColor-Flag doppelt.
        const objType = selectedObject?.data?.objectType || selectedObject?.data?.gardenObjectType;
        const showToggle =
          Boolean(objType) &&
          !SYMBOL_ONLY_TYPES.has(objType) &&
          propertyKey === getColorPropertyKey(objType);

        return (
          <PropertyColorField
            key={propertyKey}
            label={propDef.label}
            value={value}
            onChange={onChange}
            showCustomToggle={showToggle}
            useCustomColor={!!propertyValues.useCustomColor}
            onToggleCustomColor={(checked) => handlePropertyChange('useCustomColor', checked)}
          />
        );
      }

      case PROPERTY_TYPES.BOOLEAN:
        return (
          <PropertyBooleanField
            key={propertyKey}
            label={propDef.label}
            value={value}
            onChange={onChange}
          />
        );

      case PROPERTY_TYPES.SLIDER:
        return (
          <PropertySliderField
            key={propertyKey}
            label={propDef.label}
            value={value}
            onChange={onChange}
            min={propDef.min}
            max={propDef.max}
            step={propDef.step}
            unit={propDef.unit}
          />
        );

      default:
        return (
          <div key={propertyKey} className="text-xs text-gray-500">
            Unsupported property type: {propDef.type}
          </div>
        );
    }
  };


  // No object selected
  if (!selectedObject) {
    return (
      <div className="p-4">
        <div className="text-sm text-gray-500 text-center py-8">
          Wählen Sie ein Objekt aus,
          <br />
          um dessen Eigenschaften anzuzeigen.
        </div>
      </div>
    );
  }

  // No config found for object type
  if (!objectConfig) {
    return (
      <div className="p-4">
        <div className="text-sm text-gray-500 text-center py-8">
          Keine Konfiguration gefunden für diesen Objekttyp.
        </div>
      </div>
    );
  }

  const mvpProperties = objectConfig.properties?.mvp || {};

  // Sort properties by order field
  const sortedPropertyKeys = Object.keys(mvpProperties).sort((a, b) => {
    const orderA = mvpProperties[a].order ?? 999;
    const orderB = mvpProperties[b].order ?? 999;
    return orderA - orderB;
  });

  // Group dimension properties for grid layout
  const dimensionKeys = sortedPropertyKeys.filter(key =>
    GRID_LAYOUT_GROUPS.dimensions.includes(key)
  );
  const otherKeys = sortedPropertyKeys.filter(key =>
    !GRID_LAYOUT_GROUPS.dimensions.includes(key)
  );

  /**
   * Render calculated area for rectangles
   */
  const renderCalculatedArea = () => {
    if (!selectedObject || selectedObject.objectType !== GARDEN_OBJECT_TYPES.RECTANGLE) {
      return null;
    }

    const areaSqm = ((rectangleDimensions.width * rectangleDimensions.height) / 10000).toFixed(2);

    return (
      <div className="bg-gray-50 rounded p-3 border border-gray-200">
        <div className="flex justify-between items-center">
          <span className="text-xs font-medium text-gray-600">Fläche:</span>
          <span className="text-sm font-semibold text-emerald-600">{areaSqm} m²</span>
        </div>
      </div>
    );
  };

  /**
   * Render calculated area for circles
   */
  const renderCalculatedAreaForCircle = () => {
    if (!selectedObject || selectedObject.objectType !== GARDEN_OBJECT_TYPES.CIRCLE) {
      return null;
    }

    // Area = π * radiusX * radiusY (ellipse formula)
    const radiusXCm = circleDimensions.radiusX;
    const radiusYCm = circleDimensions.radiusY;
    const areaCmSq = Math.PI * radiusXCm * radiusYCm;
    const areaSqm = (areaCmSq / 10000).toFixed(2);

    return (
      <div className="bg-gray-50 rounded p-3 border border-gray-200">
        <div className="flex justify-between items-center">
          <span className="text-xs font-medium text-gray-600">Fläche:</span>
          <span className="text-sm font-semibold text-emerald-600">{areaSqm} m²</span>
        </div>
      </div>
    );
  };

  /**
   * Render shape-specific properties (read-only) based on the Fabric.js object type
   */
  const renderShapeProperties = (placement = 'collapsible') => {
    if (!selectedObject) return null;

    const shapeType = selectedObject.objectType;
    const isBezierCurve =
      shapeType === GARDEN_OBJECT_TYPES.BEZIER_CURVE && selectedObject.type === FABRIC_PATH_TYPE;
    const isPolygon =
      shapeType === GARDEN_OBJECT_TYPES.POLYGON && Boolean(selectedObject.points);

    // The bezier control-point and polygon vertex editors render as a standalone,
    // always-visible block (see main return) so they are reachable on selection
    // without expanding the collapsed "Position & Abmessungen" section. Every other
    // shape's editor stays inside that section.
    const hasStandaloneEditor = isBezierCurve || isPolygon;
    if (placement === 'standalone' && !hasStandaloneEditor) return null;
    if (placement === 'collapsible' && hasStandaloneEditor) return null;

    // LINE properties
    if (shapeType === GARDEN_OBJECT_TYPES.LINE) {
      // Use local state for display (initialized in useEffect)
      const x1Cm = lineCoordinates?.x1 ?? Math.round(selectedObject.x1 / pixelsPerCm);
      const y1Cm = lineCoordinates?.y1 ?? Math.round(selectedObject.y1 / pixelsPerCm);
      const x2Cm = lineCoordinates?.x2 ?? Math.round(selectedObject.x2 / pixelsPerCm);
      const y2Cm = lineCoordinates?.y2 ?? Math.round(selectedObject.y2 / pixelsPerCm);

      // Calculate properties from current canvas state (not local state)
      const start = { x: selectedObject.x1, y: selectedObject.y1 };
      const end = { x: selectedObject.x2, y: selectedObject.y2 };
      const linePropsPixels = calculateLineProperties(start, end);
      const lengthCm = Math.round(linePropsPixels.length / pixelsPerCm);
      const angle = Math.round(linePropsPixels.angle);

      // Handler for line coordinate changes (optimized with local state + debouncing)
      const handleLineCoordinateChange = (coord, valueCm) => {
        // Update local state immediately (no lag)
        setLineCoordinates(prev => ({
          ...prev,
          [coord]: valueCm,
        }));

        // Store old state on first change
        if (!oldPropsRef.current) {
          oldPropsRef.current = {
            x1: selectedObject.x1,
            y1: selectedObject.y1,
            x2: selectedObject.x2,
            y2: selectedObject.y2,
          };
        }

        // Debounce canvas update (200ms - faster than before)
        clearTimeout(lineDebounceTimerRef.current);
        lineDebounceTimerRef.current = setTimeout(() => {
          // Convert to pixels and update canvas
          const valuePixels = valueCm * pixelsPerCm;
          selectedObject.set({
            [coord]: valuePixels,
          });
          selectedObject.setCoords();
          onUpdate?.();

          // Create undo command
          if (oldPropsRef.current && commandStack) {
            const newProps = {
              x1: selectedObject.x1,
              y1: selectedObject.y1,
              x2: selectedObject.x2,
              y2: selectedObject.y2,
            };

            const command = new ModifyObjectCommand(
              selectedObject,
              oldPropsRef.current,
              newProps
            );
            commandStack.execute(command);

            oldPropsRef.current = null;
          }
          lineDebounceTimerRef.current = null;
        }, 200);
      };

      return (
        <div className="border-t border-gray-200 pt-4 mt-4">
          <div className="space-y-3">
            {/* Read-only calculated properties */}
            <div className="text-xs">
              <span className="text-gray-600">Länge:</span>{' '}
              <span className="font-medium">{lengthCm} cm</span>
            </div>
            <div className="text-xs">
              <span className="text-gray-600">Winkel:</span>{' '}
              <span className="font-medium">{angle}°</span>
            </div>

            {/* Editable endpoint coordinates */}
            <div className="pt-2 border-t border-gray-100">
              <div className="text-xs font-semibold text-gray-700 mb-2">Startpunkt</div>
              <div className="grid grid-cols-2 gap-2">
                <PropertyNumberField
                  label="X1"
                  value={x1Cm}
                  onChange={(value) => handleLineCoordinateChange('x1', value)}
                  unit="cm"
                  step={1}
                />
                <PropertyNumberField
                  label="Y1"
                  value={y1Cm}
                  onChange={(value) => handleLineCoordinateChange('y1', value)}
                  unit="cm"
                  step={1}
                />
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold text-gray-700 mb-2">Endpunkt</div>
              <div className="grid grid-cols-2 gap-2">
                <PropertyNumberField
                  label="X2"
                  value={x2Cm}
                  onChange={(value) => handleLineCoordinateChange('x2', value)}
                  unit="cm"
                  step={1}
                />
                <PropertyNumberField
                  label="Y2"
                  value={y2Cm}
                  onChange={(value) => handleLineCoordinateChange('y2', value)}
                  unit="cm"
                  step={1}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    // BEZIER CURVE properties
    if (shapeType === GARDEN_OBJECT_TYPES.BEZIER_CURVE && selectedObject.type === FABRIC_PATH_TYPE) {
      // Helper functions for bezier curve management
      const getCurrentBezierProps = () => ({
        path: selectedObject.path,
        data: { ...selectedObject.data },
      });

      const updatePathFromPoints = (pointsInCm) => {
        const pointsInPixels = pointsInCm.map(p => ({
          x: p.x * pixelsPerCm,
          y: p.y * pixelsPerCm,
        }));

        try {
          const pathData = pointsToBezierPath(pointsInPixels, true);

          selectedObject.set({
            path: pathData,
            data: {
              ...selectedObject.data,
              controlPoints: pointsInPixels,
            },
          });

          selectedObject.setCoords();
          selectedObject.canvas?.requestRenderAll();
        } catch (error) {
          console.warn('Failed to update path:', error);
          setBezierError('Fehler beim Aktualisieren der Kurve');
        }
      };

      const handleBezierPropertyChange = (updateFn) => {
        if (!oldPropsRef.current) {
          oldPropsRef.current = { old: getCurrentBezierProps(), new: null };
        }

        updateFn();
        onUpdate?.();

        clearTimeout(bezierDebounceTimerRef.current);
        bezierDebounceTimerRef.current = setTimeout(() => {
          if (oldPropsRef.current) {
            oldPropsRef.current.new = getCurrentBezierProps();

            const command = new ModifyObjectCommand(
              selectedObject,
              oldPropsRef.current.old,
              oldPropsRef.current.new
            );
            commandStack.execute(command);

            oldPropsRef.current = null;
          }
        }, 500);
      };

      const handleControlPointChange = (index, axis, value) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue)) return;

        handleBezierPropertyChange(() => {
          const newPoints = [...bezierControlPoints];
          newPoints[index] = { ...newPoints[index], [axis]: numValue };

          setBezierControlPoints(newPoints);
          setBezierError('');
          updatePathFromPoints(newPoints);
        });
      };

      const handleAddControlPoint = () => {
        if (bezierControlPoints.length >= MAX_CONTROL_POINTS) {
          setBezierError(`Maximum ${MAX_CONTROL_POINTS} Punkte erlaubt`);
          return;
        }

        handleBezierPropertyChange(() => {
          const lastPoint = bezierControlPoints[bezierControlPoints.length - 1] || { x: 0, y: 0 };
          const newPoints = [...bezierControlPoints, { x: lastPoint.x + 50, y: lastPoint.y + 50 }];

          setBezierControlPoints(newPoints);
          setBezierError('');
          updatePathFromPoints(newPoints);
        });
      };

      const handleRemoveControlPoint = (index) => {
        if (bezierControlPoints.length <= MIN_CONTROL_POINTS) {
          setBezierError(`Minimum ${MIN_CONTROL_POINTS} Punkte erforderlich`);
          return;
        }

        handleBezierPropertyChange(() => {
          const newPoints = bezierControlPoints.filter((_, i) => i !== index);

          setBezierControlPoints(newPoints);
          setBezierError('');
          updatePathFromPoints(newPoints);
        });
      };

      const pointCount = bezierControlPoints.length;

      // Calculate area and perimeter from control points (convert cm to pixels first)
      const pointsInPixels = bezierControlPoints.map(p => ({ x: p.x * pixelsPerCm, y: p.y * pixelsPerCm }));
      const areaPixels = pointsInPixels.length >= 3 ? calculatePolygonArea(pointsInPixels) : 0;
      const perimeterPixels = pointsInPixels.length >= 2 ? calculatePolygonPerimeter(pointsInPixels) : 0;

      // Convert to cm and m
      const areaCm2 = areaPixels / (pixelsPerCm * pixelsPerCm);
      const areaSqm = (areaCm2 / 10000).toFixed(2);
      const perimeterCm = perimeterPixels / pixelsPerCm;
      const perimeterM = (perimeterCm / 100).toFixed(2);

      return (
        <div className="border-t border-gray-200 pt-4 mt-4">
          <div className="space-y-2">
            {/* Error message if any */}
            {bezierError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {bezierError}
              </div>
            )}

            {/* Calculated properties - compact format like Zeichnen tab */}
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Punkte</span>
              <span className="font-medium">{pointCount}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Fläche</span>
              <span className="font-medium">{areaSqm} m²</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Umfang</span>
              <span className="font-medium">{perimeterM} m</span>
            </div>

            {/* Control points table - complete implementation matching Zeichnen tab */}
            <div className="pt-2">
              <div className="flex justify-between items-center mb-2">
                <span className="block text-sm font-medium text-gray-700">Kontrollpunkte</span>
                <button
                  onClick={handleAddControlPoint}
                  disabled={bezierControlPoints.length >= MAX_CONTROL_POINTS}
                  className="inline-flex items-center px-2 py-1 text-xs bg-emerald-500 text-white rounded hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  title="Punkt hinzufügen"
                >
                  <Plus size={14} className="mr-1" />
                  Hinzufügen
                </button>
              </div>

              <div className="border border-gray-200 rounded max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left font-medium text-gray-700">#</th>
                      <th className="px-2 py-1 text-left font-medium text-gray-700">X (cm)</th>
                      <th className="px-2 py-1 text-left font-medium text-gray-700">Y (cm)</th>
                      <th className="px-2 py-1 text-center font-medium text-gray-700">Entf.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bezierControlPoints.map((point, index) => (
                      <tr key={index} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-2 py-1 text-gray-600">{index + 1}</td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            value={point.x}
                            onChange={(e) => handleControlPointChange(index, 'x', e.target.value)}
                            className="w-full px-1 py-0.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            value={point.y}
                            onChange={(e) => handleControlPointChange(index, 'y', e.target.value)}
                            className="w-full px-1 py-0.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          />
                        </td>
                        <td className="px-2 py-1 text-center">
                          <button
                            onClick={() => handleRemoveControlPoint(index)}
                            disabled={bezierControlPoints.length <= MIN_CONTROL_POINTS}
                            className="text-red-500 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                            title="Punkt entfernen"
                          >
                            <X size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // POLYGON properties
    if (shapeType === GARDEN_OBJECT_TYPES.POLYGON && selectedObject.points) {
      // Helper functions for polygon management
      const getCurrentPolygonProps = () => ({
        points: selectedObject.points,
        data: { ...selectedObject.data },
      });

      const handlePolygonPropertyChange = (updateFn) => {
        if (!oldPropsRef.current) {
          oldPropsRef.current = { old: getCurrentPolygonProps(), new: null };
        }

        updateFn();
        onUpdate?.();

        clearTimeout(polygonDebounceTimerRef.current);
        polygonDebounceTimerRef.current = setTimeout(() => {
          if (oldPropsRef.current) {
            oldPropsRef.current.new = getCurrentPolygonProps();

            const command = new ModifyObjectCommand(
              selectedObject,
              oldPropsRef.current.old,
              oldPropsRef.current.new
            );
            commandStack.execute(command);

            oldPropsRef.current = null;
          }
        }, 500);
      };

      const handleVertexChange = (index, axis, value) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue)) return;

        handlePolygonPropertyChange(() => {
          const newVertices = [...polygonVertices];
          newVertices[index] = { ...newVertices[index], [axis]: numValue };

          const verticesInPixels = newVertices.map(v => ({
            x: v.x * pixelsPerCm,
            y: v.y * pixelsPerCm,
          }));

          if (hasSelfIntersection(verticesInPixels)) {
            setPolygonError('Warning: Polygon edges cross');
          } else {
            setPolygonError('');
          }

          setPolygonVertices(newVertices);
          selectedObject.set({ points: verticesInPixels });
          selectedObject.setCoords();
          // Notify vertex handles to reposition (if vertex edit mode is active)
          selectedObject.fire('modified');
          selectedObject.canvas?.requestRenderAll();
        });
      };

      const handleAddVertex = () => {
        if (polygonVertices.length >= MAX_VERTICES) {
          setPolygonError(`Maximum ${MAX_VERTICES} vertices allowed`);
          return;
        }

        handlePolygonPropertyChange(() => {
          const lastVertex = polygonVertices[polygonVertices.length - 1] || { x: 0, y: 0 };
          const newVertices = [...polygonVertices, { x: lastVertex.x + 50, y: lastVertex.y + 50 }];

          const verticesInPixels = newVertices.map(v => ({
            x: v.x * pixelsPerCm,
            y: v.y * pixelsPerCm,
          }));

          setPolygonVertices(newVertices);
          selectedObject.set({ points: verticesInPixels });
          selectedObject.setCoords();
          // Notify vertex handles to reposition (if vertex edit mode is active)
          selectedObject.fire('modified');
          selectedObject.canvas?.requestRenderAll();
          setPolygonError('');
        });
      };

      const handleRemoveVertex = (index) => {
        if (polygonVertices.length <= MIN_VERTICES) {
          setPolygonError(`Minimum ${MIN_VERTICES} vertices required`);
          return;
        }

        handlePolygonPropertyChange(() => {
          const newVertices = polygonVertices.filter((_, i) => i !== index);

          const verticesInPixels = newVertices.map(v => ({
            x: v.x * pixelsPerCm,
            y: v.y * pixelsPerCm,
          }));

          if (hasSelfIntersection(verticesInPixels)) {
            setPolygonError('Warning: Polygon edges cross');
          } else {
            setPolygonError('');
          }

          setPolygonVertices(newVertices);
          selectedObject.set({ points: verticesInPixels });
          selectedObject.setCoords();
          // Notify vertex handles to reposition (if vertex edit mode is active)
          selectedObject.fire('modified');
          selectedObject.canvas?.requestRenderAll();
        });
      };

      const vertexCount = polygonVertices.length;

      // Calculate area and perimeter from vertices (convert cm to pixels first)
      const verticesInPixels = polygonVertices.map(v => ({ x: v.x * pixelsPerCm, y: v.y * pixelsPerCm }));
      const areaPixels = verticesInPixels.length >= 3 ? calculatePolygonArea(verticesInPixels) : 0;
      const perimeterPixels = verticesInPixels.length >= 2 ? calculatePolygonPerimeter(verticesInPixels) : 0;

      // Convert to cm and m
      const areaCm2 = areaPixels / (pixelsPerCm * pixelsPerCm);
      const areaSqm = (areaCm2 / 10000).toFixed(2);
      const perimeterCm = perimeterPixels / pixelsPerCm;
      const perimeterM = (perimeterCm / 100).toFixed(2);

      return (
        <div className="border-t border-gray-200 pt-4 mt-4">
          <div className="space-y-2">
            {/* Error message if any */}
            {polygonError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {polygonError}
              </div>
            )}

            {/* Calculated properties - compact format like Zeichnen tab */}
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Punkte</span>
              <span className="font-medium">{vertexCount}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Fläche</span>
              <span className="font-medium">{areaSqm} m²</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Umfang</span>
              <span className="font-medium">{perimeterM} m</span>
            </div>

            {/* Vertices table - complete implementation matching Zeichnen tab */}
            <div className="pt-2">
              <div className="flex justify-between items-center mb-2">
                <span className="block text-sm font-medium text-gray-700">Punkte</span>
                <button
                  onClick={handleAddVertex}
                  disabled={polygonVertices.length >= MAX_VERTICES}
                  className="inline-flex items-center px-2 py-1 text-xs bg-emerald-500 text-white rounded hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  title="Punkt hinzufügen"
                >
                  <Plus size={14} className="mr-1" />
                  Hinzufügen
                </button>
              </div>

              <div className="border border-gray-200 rounded max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left font-medium text-gray-700">#</th>
                      <th className="px-2 py-1 text-left font-medium text-gray-700">X (cm)</th>
                      <th className="px-2 py-1 text-left font-medium text-gray-700">Y (cm)</th>
                      <th className="px-2 py-1 text-center font-medium text-gray-700">Entf.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {polygonVertices.map((vertex, index) => (
                      <tr key={index} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-2 py-1 text-gray-600">{index + 1}</td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            value={vertex.x}
                            onChange={(e) => handleVertexChange(index, 'x', e.target.value)}
                            className="w-full px-1 py-0.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            value={vertex.y}
                            onChange={(e) => handleVertexChange(index, 'y', e.target.value)}
                            className="w-full px-1 py-0.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          />
                        </td>
                        <td className="px-2 py-1 text-center">
                          <button
                            onClick={() => handleRemoveVertex(index)}
                            disabled={polygonVertices.length <= MIN_VERTICES}
                            className="text-red-500 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                            title="Punkt entfernen"
                          >
                            <X size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // ========================================================================
    // RECTANGLE PROPERTIES
    // ========================================================================

    if (shapeType === GARDEN_OBJECT_TYPES.RECTANGLE) {
      // Helper functions
      const getCurrentRectangleProps = () => ({
        width: selectedObject.width,
        height: selectedObject.height,
        scaleX: selectedObject.scaleX,
        scaleY: selectedObject.scaleY,
        left: selectedObject.left,
        top: selectedObject.top,
      });

      const handleRectanglePropertyChange = (updateFn) => {
        if (!oldPropsRef.current) {
          oldPropsRef.current = { old: getCurrentRectangleProps(), new: null };
        }
        updateFn();
        onUpdate?.();
        clearTimeout(rectangleDebounceTimerRef.current);
        rectangleDebounceTimerRef.current = setTimeout(() => {
          if (oldPropsRef.current) {
            oldPropsRef.current.new = getCurrentRectangleProps();
            const command = new ModifyObjectCommand(
              selectedObject,
              oldPropsRef.current.old,
              oldPropsRef.current.new
            );
            commandStack.execute(command);
            oldPropsRef.current = null;
          }
        }, 500);
      };

      const handleDimensionChange = (field, value) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue) || numValue < 0) return;

        handleRectanglePropertyChange(() => {
          setRectangleDimensions(prev => ({ ...prev, [field]: numValue }));

          const widthPixels = field === 'width' ? numValue * pixelsPerCm : rectangleDimensions.width * pixelsPerCm;
          const heightPixels = field === 'height' ? numValue * pixelsPerCm : rectangleDimensions.height * pixelsPerCm;

          selectedObject.set({
            width: widthPixels,
            height: heightPixels,
            scaleX: 1,
            scaleY: 1,
          });
          selectedObject.setCoords();
          selectedObject.canvas?.requestRenderAll();
        });
      };

      const handlePositionChange = (field, value) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue)) return;

        handleRectanglePropertyChange(() => {
          setRectanglePosition(prev => ({ ...prev, [field]: numValue }));

          const leftPixels = field === 'x' ? numValue * pixelsPerCm : rectanglePosition.x * pixelsPerCm;
          const topPixels = field === 'y' ? numValue * pixelsPerCm : rectanglePosition.y * pixelsPerCm;

          selectedObject.set({
            left: leftPixels,
            top: topPixels,
          });
          selectedObject.setCoords();
          selectedObject.canvas?.requestRenderAll();
        });
      };

      return (
        <>
          {/* Position - appears first */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Position</h4>
            <div className="grid grid-cols-2 gap-3">
              {/* X Position */}
              <PropertyNumberField
                label="X"
                value={rectanglePosition.x}
                onChange={(value) => handlePositionChange('x', value.toString())}
                unit="cm"
                step={1}
              />

              {/* Y Position */}
              <PropertyNumberField
                label="Y"
                value={rectanglePosition.y}
                onChange={(value) => handlePositionChange('y', value.toString())}
                unit="cm"
                step={1}
              />
            </div>
          </div>

          {/* Dimensions - Länge & Breite */}
          <div className="grid grid-cols-2 gap-3">
            {/* Length (was Width) */}
            <PropertyNumberField
              label="Länge"
              value={rectangleDimensions.width}
              onChange={(value) => handleDimensionChange('width', value.toString())}
              unit="cm"
              min={1}
              step={1}
            />

            {/* Width (was Height) */}
            <PropertyNumberField
              label="Breite"
              value={rectangleDimensions.height}
              onChange={(value) => handleDimensionChange('height', value.toString())}
              unit="cm"
              min={1}
              step={1}
            />
          </div>
        </>
      );
    }

    // CIRCLE properties
    if (shapeType === GARDEN_OBJECT_TYPES.CIRCLE) {
      // Helper functions
      const getCurrentCircleProps = () => ({
        rx: selectedObject.rx,
        ry: selectedObject.ry,
        scaleX: selectedObject.scaleX,
        scaleY: selectedObject.scaleY,
        left: selectedObject.left,
        top: selectedObject.top,
      });

      const handleCirclePropertyChange = (updateFn) => {
        if (!oldPropsRef.current) {
          oldPropsRef.current = { old: getCurrentCircleProps(), new: null };
        }
        updateFn();
        onUpdate?.();
        clearTimeout(circleDebounceTimerRef.current);
        circleDebounceTimerRef.current = setTimeout(() => {
          if (oldPropsRef.current) {
            oldPropsRef.current.new = getCurrentCircleProps();
            const command = new ModifyObjectCommand(
              selectedObject,
              oldPropsRef.current.old,
              oldPropsRef.current.new
            );
            commandStack.execute(command);
            oldPropsRef.current = null;
          }
        }, 500);
      };

      const handleRadiusChange = (axis, value) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue) || numValue < 1) return;

        handleCirclePropertyChange(() => {
          setCircleDimensions(prev => ({ ...prev, [axis]: numValue }));

          const radiusXPixels = axis === 'radiusX' ? numValue * pixelsPerCm : circleDimensions.radiusX * pixelsPerCm;
          const radiusYPixels = axis === 'radiusY' ? numValue * pixelsPerCm : circleDimensions.radiusY * pixelsPerCm;

          selectedObject.set({
            rx: radiusXPixels,
            ry: radiusYPixels,
            scaleX: 1,
            scaleY: 1,
          });
          selectedObject.setCoords();
          selectedObject.canvas?.requestRenderAll();
        });
      };

      const handleCirclePositionChange = (field, value) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue)) return;

        handleCirclePropertyChange(() => {
          setCirclePosition(prev => ({ ...prev, [field]: numValue }));

          const leftPixels = field === 'x' ? numValue * pixelsPerCm : circlePosition.x * pixelsPerCm;
          const topPixels = field === 'y' ? numValue * pixelsPerCm : circlePosition.y * pixelsPerCm;

          selectedObject.set({
            left: leftPixels,
            top: topPixels,
          });
          selectedObject.setCoords();
          selectedObject.canvas?.requestRenderAll();
        });
      };

      return (
        <>
          {/* Position - appears first */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Position</h4>
            <div className="grid grid-cols-2 gap-3">
              {/* X Position */}
              <PropertyNumberField
                label="X"
                value={circlePosition.x}
                onChange={(value) => handleCirclePositionChange('x', value.toString())}
                unit="cm"
                step={1}
              />

              {/* Y Position */}
              <PropertyNumberField
                label="Y"
                value={circlePosition.y}
                onChange={(value) => handleCirclePositionChange('y', value.toString())}
                unit="cm"
                step={1}
              />
            </div>
          </div>

          {/* Radii - Radius X & Radius Y */}
          <div className="grid grid-cols-2 gap-3">
            <PropertyNumberField
              label="Radius X"
              value={circleDimensions.radiusX}
              onChange={(value) => handleRadiusChange('radiusX', value.toString())}
              unit="cm"
              min={1}
              step={1}
            />
            <PropertyNumberField
              label="Radius Y"
              value={circleDimensions.radiusY}
              onChange={(value) => handleRadiusChange('radiusY', value.toString())}
              unit="cm"
              min={1}
              step={1}
            />
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col bg-white">
      {/* Header with object type name */}
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="text-base font-semibold text-gray-900">
          Eigenschaften - {objectConfig.name}
        </h3>
      </div>

      {/* Properties */}
      <div className="px-4 py-4 space-y-4">
        {sortedPropertyKeys.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-4">
            Keine Eigenschaften definiert.
          </div>
        ) : (
          <>
            {/* Render name first (if exists) */}
            {otherKeys.filter(key => key === 'name').map(key => (
              <div key={key}>
                {renderField(key, mvpProperties[key])}
              </div>
            ))}

            {/* #720: Bestandsweg (Legacy-Fläche). Breite/Verlauf sind für ihn
                nicht bedienbar — Hinweis zum Umstellen auf das Mittellinien-Modell. */}
            {objectTypeId === 'weg' && selectedObject.data?.shapeType !== 'centerline' && (
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Bestehender Weg als Fläche. Breite und Verlauf lassen sich hier
                nicht ändern — zum Umstellen den Weg neu zeichnen.
              </div>
            )}

            {/* Bezier control-point / polygon vertex editor — always visible on
                selection, outside the collapsed section (see #463) */}
            {renderShapeProperties('standalone')}

            {/* Collapsible Position & Abmessungen Section */}
            <div className="border border-gray-200 rounded">
              {/* Collapsible Header */}
              <button
                onClick={() => setIsPositionCollapsed(!isPositionCollapsed)}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors rounded-t"
                type="button"
              >
                <span className="text-sm font-semibold text-gray-700">Position & Abmessungen</span>
                {isPositionCollapsed ? (
                  <ChevronDown size={18} className="text-gray-600" />
                ) : (
                  <ChevronUp size={18} className="text-gray-600" />
                )}
              </button>

              {/* Collapsible Content */}
              {!isPositionCollapsed && (
                <div className="px-3 py-3 space-y-3">
                  {/* Render dimension fields in grid */}
                  {dimensionKeys.length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      {dimensionKeys.map(key => renderField(key, mvpProperties[key]))}
                    </div>
                  )}

                  {/* Render shape-specific properties (Position, Länge/Breite) */}
                  {renderShapeProperties('collapsible')}

                  {/* Render Höhe field if exists */}
                  {otherKeys.filter(key => key === 'height').map(key => (
                    <div key={key}>
                      {renderField(key, mvpProperties[key])}
                    </div>
                  ))}

                  {/* Render calculated Fläche for rectangles */}
                  {renderCalculatedArea()}

                  {/* Render calculated Fläche for circles */}
                  {renderCalculatedAreaForCircle()}
                </div>
              )}
            </div>

            {/* Render remaining other fields (Material, Farbe, etc.) */}
            {otherKeys
              .filter(key => key !== 'name' && key !== 'height')
              .filter(key => isPropertyFieldVisible(objectTypeId, key, propertyValues, selectedObject.data?.shapeType))
              .map(key => (
                <div key={key}>
                  {renderField(key, mvpProperties[key])}
                </div>
              ))}
          </>
        )}
      </div>

      {/* Delete Button */}
      <div className="px-4 py-3 border-t border-gray-200">
        <button
          onClick={() => setShowDeleteDialog(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2
            bg-red-50 border border-red-300 text-red-700 rounded
            hover:bg-red-100 hover:border-red-400 transition-all font-medium text-sm"
        >
          <Trash2 size={16} />
          Löschen
        </button>
      </div>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onConfirm={() => {
          setShowDeleteDialog(false);
          onDelete?.(selectedObject);
        }}
        onCancel={() => setShowDeleteDialog(false)}
        title={`${objectConfig.name} löschen`}
        message={`Möchten Sie dieses ${objectConfig.name} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
        variant="danger"
      />
    </div>
  );
};

export default DynamicGardenObjectProperties;
